import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// --- Country + Document Type regex patterns ---
const PASSPORT_PATTERN = /^[A-Z0-9]{6,9}$/i;
const NATIONAL_ID_PATTERNS: Record<string, RegExp> = {
  NG: /^[A-Z0-9-]{11}$/i,        // Nigeria NIN
  US: /^[0-9]{9}$/,               // US SSN (digits only)
  GB: /^[A-Z0-9]{9,13}$/i,       // UK National Insurance style
  IN: /^[0-9]{12}$/,              // India Aadhaar
  ZA: /^[0-9]{13}$/,             // South Africa
};
const DRIVERS_LICENSE_PATTERNS: Record<string, RegExp> = {
  US: /^[A-Z0-9-]{4,20}$/i,
  GB: /^[A-Z0-9]{16}$/i,
  NG: /^[A-Z0-9-]{10,12}$/i,
};
const DEFAULT_ID_PATTERN = /^[A-Z0-9-]{5,30}$/i;

export function idNumberValidator(getDocType: () => string, getCountryCode: () => string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = (control.value || '').trim();
    if (!value) return null; // let Validators.required handle empty

    if (value.length < 5 || value.length > 30) {
      return { idNumberLength: true };
    }

    const docType = getDocType();
    const country = getCountryCode();

    let pattern: RegExp;
    if (docType === 'passport' || docType === 'International Passport') {
      pattern = PASSPORT_PATTERN;
    } else if (docType === 'national_id' || docType === 'National ID') {
      pattern = NATIONAL_ID_PATTERNS[country] || DEFAULT_ID_PATTERN;
    } else if (docType === 'drivers_license' || docType === "Driver's License") {
      pattern = DRIVERS_LICENSE_PATTERNS[country] || DEFAULT_ID_PATTERN;
    } else {
      pattern = DEFAULT_ID_PATTERN;
    }

    return pattern.test(value) ? null : { idNumberFormat: true };
  };
}

/** Expiry date must be in the future and at least 6 months from today, max +20 years */
export function expiryDateValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;
  const expiry = new Date(control.value);
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  if (expiry <= now) {
    return { expiryPast: true };
  }

  const sixMonths = new Date();
  sixMonths.setMonth(sixMonths.getMonth() + 6);
  sixMonths.setHours(0, 0, 0, 0);
  if (expiry < sixMonths) {
    return { expiryTooSoon: true };
  }

  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 20);
  if (expiry > maxDate) {
    return { expiryTooFar: true };
  }

  return null;
}

/** Issue date must be before expiry date and not in the future */
export function issueDateValidator(getExpiryDate: () => string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const issue = new Date(control.value);
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    if (issue > now) {
      return { issueFuture: true };
    }

    const expiryStr = getExpiryDate();
    if (expiryStr) {
      const expiry = new Date(expiryStr);
      if (issue >= expiry) {
        return { issueAfterExpiry: true };
      }
    }

    return null;
  };
}

/** Flyer number: 5-30 chars, A-Z, 0-9, hyphen */
export function flyerNumberValidator(control: AbstractControl): ValidationErrors | null {
  const value = (control.value || '').trim();
  if (!value) return null;
  if (value.length < 5 || value.length > 30) {
    return { flyerNumberLength: true };
  }
  if (!/^[A-Z0-9-]+$/i.test(value)) {
    return { flyerNumberFormat: true };
  }
  return null;
}

/** Mask an ID string: show first 4 + last 2, mask middle with * */
export function maskIdNumber(value: string): string {
  if (!value || value.length <= 6) return value;
  const first = value.slice(0, 4);
  const last = value.slice(-2);
  const masked = '*'.repeat(value.length - 6);
  return `${first}${masked}${last}`;
}
